import {
  Component,
  Element,
  Fragment,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { navStateStore } from "@phn/state/nav-state-store";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { getHTMLElement, isDesktop } from "@phn/utility/helper";
import { getImage } from "@phn/utility/navi-state";
import type { BodyType, Series, TLink } from "@phn/entities/content";
import * as datalayerEvents from "./data-layer-events";

@Component({
  tag: "phn-nd-car-body-types-drawer",
  styleUrl: "car-body-types-drawer.scss",
  scoped: true,
})
export class CarBodyTypeDrawer {
  @Prop() series!: Series;

  @Prop() breakpoint!: number;

  @Prop() modelType?: string;

  @State() showSkeleton =
    !navStateStore.state.preloadedSeriesImages[this.series.id];

  @State() countLoadedImages = 0;

  @Element() el!: HTMLPhnNdCarBodyTypesDrawerElement;

  @Watch("series")
  seriesWatcher(series: Series): void {
    this.countLoadedImages = 0;
    this.showSkeleton = !navStateStore.state.preloadedSeriesImages[series.id];
  }

  @Listen("imageLoad")
  async imageLoadWatcher(): Promise<void> {
    this.countLoadedImages++;
    if (this.series.bodyTypes.length === this.countLoadedImages) {
      await componentsOnReady(this.el, new Timeout(0));
      this.showSkeleton = false;
      navStateStore.state.preloadedSeriesImages[this.series.id] = true;
    }
  }

  private getImageSource(bodyType: BodyType): string {
    const sideShot = bodyType?.images?.sideShot.src ?? "placeholder";
    return sideShot.indexOf("imgix") > -1
      ? this.adaptImgixUrl(sideShot)
      : this.adaptStoryblokImageUrl(sideShot);
  }

  private adaptImgixUrl(url: string): string {
    return `${url}?w=400&auto=format&q=50 1x,
      ${url}?w=800&auto=format&q=50 2x`;
  }

  private adaptStoryblokImageUrl(url: string): string {
    return `${url}/m/400x0/filters:quality(50) 1x,
      ${url}/m/800x0/filters:quality(50) 2x`;
  }

  private getBodyTypeAspectRatio(bodyType: BodyType): string {
    const { width = 160, height = 47 } = bodyType?.images?.sideShot ?? {};
    return `${width}/${height}`;
  }

  /**
   * Model links we get from content via mpi are statically set to porsche.com, which causes expensive redirects in China.
   * @param linkoutUrl
   * @private
   */
  private adaptLinkForCNDomain(linkoutUrl: string): string {
    if (
      window.location.hostname.includes("porsche.cn") &&
      linkoutUrl.includes("www.porsche.com")
    ) {
      return linkoutUrl.replace("www.porsche.com", "www.porsche.cn");
    }
    return linkoutUrl;
  }

  private getLinks(links: TLink[]): [TLink[], TLink[]] {
    return links.reduce(
      (acc, curr) => {
        const [nonSticky, stickyLinks] = acc;
        if (curr.id.endsWith("configure") || curr.id.endsWith("findvehicles")) {
          stickyLinks.push({
            ...curr,
            link: this.adaptLinkForCNDomain(curr.link),
          });
        } else {
          nonSticky.push({
            ...curr,
            link: this.adaptLinkForCNDomain(curr.link),
          });
        }
        return acc;
      },
      [[], []],
    );
  }

  render(): HTMLPhnNdCarBodyTypesDrawerElement {
    const [nonSticky, stickyLinks] = this.getLinks(this.series.links);
    const { bodyTypes } = this.series;
    const isOutOfProduction = bodyTypes.some((bt) => bt.outOfProduction);

    return (
      <Host>
        <Fragment>
          {isDesktop(this.breakpoint) ? (
            <phn-nd-signature-heading text={navStateStore.state.modelType} />
          ) : null}

          {isOutOfProduction ? null : (
            <phn-p-link-pure
              align-label="start"
              href={this.series.all.link}
              size="medium"
              class="all-link"
              data-test-id="car-body-types-drawer-further-links-button"
              onClick={() => {
                datalayerEvents.sendNavigationDataLayerEvent(this.series);
              }}
            >
              {this.series.all.text}
            </phn-p-link-pure>
          )}

          <ul
            class={`${this.series.name} ${isOutOfProduction ? "out-of-production" : ""}`}
          >
            {bodyTypes.map((bodyType) => (
              <li class="series-body-type" key={bodyType.id}>
                <a
                  href={this.adaptLinkForCNDomain(bodyType.link)}
                  class="model-link"
                  data-id={bodyType.id}
                  onClick={(e: MouseEvent) => {
                    const clickedElement = getHTMLElement(e.target);
                    if (clickedElement !== null) {
                      datalayerEvents.sendBodyTypeClickDataLayerEvent(
                        bodyType,
                        clickedElement.tagName,
                      );
                    }
                  }}
                >
                  <phn-model-tile
                    slot="model-area"
                    aspectRatio={this.getBodyTypeAspectRatio(bodyType)}
                    breakpoint={this.breakpoint}
                    modelSeries={bodyType}
                    srcSet={this.getImageSource(bodyType)}
                  />
                </a>
              </li>
            ))}
            {this.series.modelBanners?.map((banner) => (
              <li class="series-body-type">
                <a
                  href={this.adaptLinkForCNDomain(banner.link)}
                  class="model-link"
                  key={banner.name}
                  data-id={banner.id}
                  onClick={(e: MouseEvent) => {
                    const clickedElement = getHTMLElement(e.target);
                    if (clickedElement !== null) {
                      datalayerEvents.sendModelBannerClickDataLayerEvent(
                        banner,
                        clickedElement.tagName,
                      );
                    }
                  }}
                >
                  <phn-model-banner-tile
                    slot="model-area"
                    breakpoint={this.breakpoint}
                    text={banner.name}
                    srcSet={getImage(banner.image, navStateStore.state.env)}
                    tags={banner.tags}
                    addGradient={banner.addGradient}
                  ></phn-model-banner-tile>
                </a>
              </li>
            ))}
            {isOutOfProduction
              ? null
              : nonSticky.map((link, index) => (
                  <li
                    key={`non-sticky-${link.id}`}
                    class={`non-sticky-link ${index === 0 ? "first" : ""}`}
                  >
                    <phn-p-link-pure
                      stretch={true}
                      class="link"
                      align-label="start"
                      size="medium"
                      href={link.link}
                      onClick={() => {
                        datalayerEvents.sendMetaFunctionDataLayerEvent(link);
                      }}
                    >
                      {link.text}
                    </phn-p-link-pure>
                  </li>
                ))}
          </ul>

          {isOutOfProduction ? null : (
            <ul class="sticky-links">
              {stickyLinks.map((link) => (
                <li key={link.id}>
                  <phn-p-link
                    variant={
                      link.id.endsWith("configure") ? "primary" : "secondary"
                    }
                    href={link.link}
                    size="medium"
                    class="link"
                    onClick={() => {
                      datalayerEvents.sendMetaFunctionDataLayerEvent(link);
                    }}
                  >
                    {link.text}
                  </phn-p-link>
                </li>
              ))}
            </ul>
          )}
        </Fragment>
      </Host>
    ) as HTMLPhnNdCarBodyTypesDrawerElement;
  }
}
